import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Router } from '@angular/router';
import { combineLatest, debounceTime, distinctUntilChanged, finalize, map, mergeMap, Observable, startWith, Subscription } from 'rxjs';
import { RefreshService } from '../../components/common/refresh-widget/refresh.service';
import { Event, EventsApiService, EventSearchCriteria, EventSeverity } from '../../api/events-api.service';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCheckboxModule } from '@angular/material/checkbox';

const EVENT_STATUS_ORDER = {
  CREATED: 0,
  ACKNOWLEDGED: 1,
  RESOLVED: 2,
};
const EVENT_SEVERITY_ORDER = {
  INFO: 2,
  WARNING: 1,
  CRITICAL: 0,
};

@Component({
  selector: 'app-events-page',
  templateUrl: './events-page.component.html',
  styleUrls: ['./events-page.component.scss'],
  standalone: true,
  imports: [
    /* Angular */
    CommonModule,
    ReactiveFormsModule,
    /* Material */
    MatToolbarModule,
    MatProgressBarModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatListModule,
    MatMenuModule,
    MatButtonModule,
    MatTooltipModule,
    MatSnackBarModule,
    MatCheckboxModule,
  ]
})
export class EventsPageComponent implements OnDestroy {
  public searchedEvents$: Observable<Event[]>;
  public events$: Observable<Event[]>;
  public eventsCount$: Observable<{[key: string]: number}>;
  public searchFormGroup = new FormGroup({
    severity: new FormControl('all'),
    chargepointId: new FormControl(''),
    includeResolved: new FormControl(false),
  });
  public orderByFormControl = new FormControl('severity'); 

  public searching = false;

  private subscriptions: Subscription[] = [];

  constructor(private eventsApiService: EventsApiService, private router: Router, private refreshService: RefreshService, private snackBar: MatSnackBar) {
    this.searchedEvents$ = this.refreshService.refreshTrigger.pipe(
      mergeMap(() => this.doSearch())
    );
    this.subscriptions.push(
      this.searchFormGroup.valueChanges.pipe(
        debounceTime(500),
        distinctUntilChanged(),
      ).subscribe(() => this.refreshService.trigger())
    );

    const orderBy$ = this.orderByFormControl.valueChanges.pipe(
      startWith('severity'),
    );

    const COMPARATORS = {
      createdAt: (a: Event, b: Event) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0),
      status: (a: Event, b: Event) => (EVENT_STATUS_ORDER[a.status ?? ''] ?? 0) - (EVENT_STATUS_ORDER[b.status ?? ''] ?? 0),
      severity: (a: Event, b: Event) => (EVENT_SEVERITY_ORDER[a.severity ?? ''] ?? 0) - (EVENT_SEVERITY_ORDER[b.severity ?? ''] ?? 0),
    };

    this.events$ = combineLatest([this.searchedEvents$, orderBy$]).pipe(
      map(([events, orderBy]) => events.sort((a, b) => {
        const comparatorKeys = [orderBy, ...["severity", "status", "createdAt"].filter(key => key !== orderBy)];
        for (const key of comparatorKeys) {
          const comparator = COMPARATORS[key as keyof typeof COMPARATORS];
          if (comparator && comparator(a, b) !== 0) {
            return comparator(a, b);
          }
        }
        return 0;
      }))
    );
    this.eventsCount$ = this.searchedEvents$.pipe(
      map(events => events.reduce((acc, event) => {
        acc[event.severity ?? ''] = (acc[event.severity ?? ''] ?? 0) + 1;
        return acc;
      }, {} as {[key: string]: number}))
    );
  }

  private doSearch() {
    function toEventSeverity(val?: string | null): EventSeverity | null {
      if (val && ['INFO', 'WARNING', 'CRITICAL'].includes(val)) {
        return val as EventSeverity;
      }
      return null;
    }
    const searchCriteria: EventSearchCriteria = {
      severity: toEventSeverity(this.searchFormGroup.value.severity),
      chargepointId: this.searchFormGroup.value.chargepointId ?? undefined,
      includeResolved: this.searchFormGroup.value.includeResolved ?? false,
    };
    this.searching = true;
    return this.eventsApiService.searchEvents(searchCriteria).pipe(
      finalize(() => this.searching = false)
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  public navigateToChargepoint(chargepointId: string) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/chargepoints', chargepointId])
    );
    window.open(url, '_blank');
  }

  public copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).then(() => {
      this.snackBar.open('Copied to clipboard', 'Close', {
        duration: 2000,
      });
    });
  }

  public eventCountTooltipLabel(severity: string) {
    switch (severity) {
      case 'INFO':
        return 'Events with severity INFO';
      case 'WARNING':
        return 'Events with severity WARNING';
      case 'CRITICAL':
        return 'Events with severity CRITICAL';
    }
    return null;
  }
}
